import { useMemo, useState } from "react";
import { Button, message } from "antd";
import type { MembershipProduct, SpaceProfile } from "../SpaceType";
import { listBundlesContainingProduct } from "../spacePageUtils";
import { SpaceProductPurchaseModal } from "./SpaceProductPurchaseModal";
import { SpaceProductUpgradeModal } from "./SpaceProductUpgradeModal";
import { BundlePlanPickerModal } from "./BundlePlanPickerModal";
import { StandaloneProductPurchaseModal } from "../../../standalone/purchase/StandaloneProductPurchaseModal";

export function BundlePlanPurchaseActions(props: {
  spaceId: string;
  row: MembershipProduct;
  membershipProducts: MembershipProduct[];
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  spaceProfile: SpaceProfile | null;
  variant: "platform" | "standalone";
  /** 默认「套餐购买」；非独售产品传「购买包含此服务的套餐」 */
  bundleCtaLabel?: string;
}): JSX.Element | null {
  const {
    spaceId,
    row,
    membershipProducts,
    isLoggedIn,
    onLoginRequest,
    spaceProfile,
    variant,
    bundleCtaLabel,
  } = props;
  const bundles = useMemo(
    () => listBundlesContainingProduct(membershipProducts, row.spaceProductId),
    [membershipProducts, row.spaceProductId]
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bundlePurchase, setBundlePurchase] = useState<MembershipProduct | null>(null);
  const [bundleUpgrade, setBundleUpgrade] = useState<MembershipProduct | null>(null);

  const upgradeEligible = (bundle: MembershipProduct) =>
    bundle.upgradeEligible === true && !!bundle.upgradeSourceSpaceProductId?.trim();

  if (bundles.length === 0) {
    return null;
  }

  const openBundleFlow = (bundle: MembershipProduct) => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (upgradeEligible(bundle)) {
      setPickerOpen(false);
      setBundleUpgrade(bundle);
      return;
    }
    if (bundle.purchaseBlocked) {
      message.info(
        bundle.purchaseBlockHint?.trim() ||
          "您已拥有相关服务权益，无法重复购买包含相同服务的产品"
      );
      return;
    }
    setPickerOpen(false);
    setBundlePurchase(bundle);
  };

  const onPlanPurchaseClick = () => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (bundles.length === 1) {
      openBundleFlow(bundles[0]);
      return;
    }
    setPickerOpen(true);
  };

  const ctaLabel =
    bundles.length === 1 && upgradeEligible(bundles[0])
      ? "升级套餐"
      : bundleCtaLabel?.trim() || "套餐购买";

  return (
    <>
      <Button
        type="primary"
        className="space-access-gate__bundle-plan-btn"
        onClick={onPlanPurchaseClick}
      >
        {ctaLabel}
      </Button>
      <BundlePlanPickerModal
        open={pickerOpen}
        bundles={bundles}
        spaceId={spaceId}
        isLoggedIn={isLoggedIn}
        onLoginRequest={onLoginRequest}
        spaceProfile={spaceProfile}
        onClose={() => setPickerOpen(false)}
        onPickBundle={openBundleFlow}
      />
      {variant === "platform" && bundlePurchase ? (
        <SpaceProductPurchaseModal
          spaceId={spaceId}
          row={bundlePurchase}
          open
          onClose={() => setBundlePurchase(null)}
          isLoggedIn={isLoggedIn}
          onLoginRequest={onLoginRequest}
          spaceProfile={spaceProfile}
        />
      ) : variant === "standalone" && bundlePurchase ? (
        <StandaloneProductPurchaseModal
          row={bundlePurchase}
          open
          onClose={() => setBundlePurchase(null)}
          isLoggedIn={isLoggedIn}
          onLoginRequest={onLoginRequest}
        />
      ) : null}
      {variant === "platform" && bundleUpgrade ? (
        <SpaceProductUpgradeModal
          spaceId={spaceId}
          row={bundleUpgrade}
          open
          onClose={() => setBundleUpgrade(null)}
          isLoggedIn={isLoggedIn}
          onLoginRequest={onLoginRequest}
          spaceProfile={spaceProfile}
        />
      ) : null}
    </>
  );
}
